import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { X, Bell, ArrowRight, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const SESSION_KEY = 'entrepot_exit_shown';
const SUB_KEY = 'entrepot_newsletter_sub';

const ExitIntentPopup = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const isAdminPage = location.pathname.startsWith('/admin') || location.pathname === '/login';

  const showPopup = useCallback(() => {
    const alreadyShown = sessionStorage.getItem(SESSION_KEY);
    const alreadySubscribed = localStorage.getItem(SUB_KEY);
    if (alreadyShown || alreadySubscribed || isAdminPage) return;

    sessionStorage.setItem(SESSION_KEY, '1');
    setVisible(true);
  }, [isAdminPage]);

  useEffect(() => {
    if (isAdminPage) return;

    // Only trigger when user actually tries to close/leave the page
    const handleBeforeUnload = (e) => {
      const alreadyShown = sessionStorage.getItem(SESSION_KEY);
      const alreadySubscribed = localStorage.getItem(SUB_KEY);
      if (alreadyShown || alreadySubscribed) return;
      
      // Show popup and cancel the unload
      e.preventDefault();
      e.returnValue = '';
      showPopup();
    };

    // Delay listener so it doesn't interfere with page load
    const timer = setTimeout(() => {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }, 10000);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isAdminPage, showPopup]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || submitting) return;

    setSubmitting(true);
    try {
      await axios.post(`${API}/api/newsletter/subscribe`, {
        email: email.trim(),
        source: 'exit_intent',
      });
      localStorage.setItem(SUB_KEY, '1');
      setSuccess(true);
      setTimeout(() => setVisible(false), 3000);
    } catch {
      // Still mark as shown, don't annoy user
      setSuccess(true);
      setTimeout(() => setVisible(false), 3000);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setVisible(false);
    // Close the tab/window when user dismisses
    window.close();
    // Fallback: if window.close() is blocked by browser, navigate back
    if (!window.closed) {
      window.history.back();
    }
  };

  if (!visible) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-[2px] z-[9990] transition-opacity"
        onClick={handleClose}
      />

      {/* Popup */}
      <div
        className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[9991] w-[90vw] max-w-md animate-in zoom-in-95 fade-in duration-300"
        data-testid="exit-intent-popup"
      >
        <div className="bg-slate-900 rounded-2xl overflow-hidden shadow-2xl border border-slate-700/50">
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-3 right-3 text-slate-500 hover:text-white transition-colors z-10"
            data-testid="exit-intent-close"
          >
            <X className="h-5 w-5" />
          </button>

          {/* Content */}
          <div className="px-6 pt-8 pb-6 text-center">
            {success ? (
              <div className="py-4">
                <div className="w-14 h-14 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-7 w-7 text-green-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{t('exitIntent.successTitle')}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{t('exitIntent.successMessage')}</p>
              </div>
            ) : (
              <>
                {/* Icon */}
                <div className="w-14 h-14 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-5">
                  <Bell className="h-7 w-7 text-blue-400" />
                </div>

                {/* Text */}
                <h3 className="text-xl font-bold text-white mb-2 leading-snug">
                  {t('exitIntent.title')}
                </h3>
                <p className="text-sm text-slate-400 mb-6 leading-relaxed">
                  {t('exitIntent.subtitle')}
                </p>

                {/* Email form */}
                <form onSubmit={handleSubmit} className="flex gap-2">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('exitIntent.emailPlaceholder')}
                    required
                    className="flex-1 bg-slate-800 border border-slate-700 rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-blue-500 transition-colors"
                    data-testid="exit-intent-email"
                    autoFocus
                  />
                  <button
                    type="submit"
                    disabled={submitting}
                    className="bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-sm font-medium rounded-xl px-5 py-2.5 transition-colors flex items-center gap-1.5 whitespace-nowrap"
                    data-testid="exit-intent-submit"
                  >
                    {submitting ? t('exitIntent.subscribing') : t('exitIntent.subscribe')}
                    {!submitting && <ArrowRight className="h-4 w-4" />}
                  </button>
                </form>

                {/* No thanks */}
                <button
                  onClick={handleClose}
                  className="text-xs text-slate-600 hover:text-slate-400 mt-4 transition-colors"
                  data-testid="exit-intent-dismiss"
                >
                  {t('exitIntent.noThanks')}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ExitIntentPopup;
